import {
  ColDef,
  type ICellRendererParams,
  ValueGetterParams,
} from "ag-grid-community";
import { AllTypeResourceItem } from "@/store/resources-grid/utils/types/resources";
import { readDataByDotSeparatedString } from "@/components/pages/common/ag-grid/utils/helpers/readDataByDotSeparatedString";
import { resourceIdentifierColumnDefColId } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/getResourceIdentifierColumnDef";
import { listingPriceColumnDefField } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/listingPriceColumnDef";
import { billingCostColumnDefField } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/billingCostColumnDef";

const colDefsThatNoNeedToReadCellRenderer = [
  // Common
  resourceIdentifierColumnDefColId,
  listingPriceColumnDefField,
  billingCostColumnDefField,

  // AWS Dynamo DB
  "resource.read_capacity_mode",
  "resource.write_capacity_mode",

  // AWS Load Balancer
  "resource.targets",

  // AWS NAT Gateway
  "resource.public_addresses",
  "resource.network_interface_ids",
  "resource.private_addresses",
];

const colDefsThatNoNeedToValueGetter = [resourceIdentifierColumnDefColId];

export const stringifyResourceColumnDef = (
  col: ColDef,
  resource: AllTypeResourceItem,
  ignoreCellRenderer?: boolean,
) => {
  const cellRendererValue = getValueFromCellRenderer(
    col,
    resource,
    ignoreCellRenderer,
  );
  if (cellRendererValue) {
    return cellRendererValue;
  }

  const valueGetterValue = getValueFromValueGetter(col, resource);
  if (valueGetterValue) {
    return valueGetterValue;
  }

  try {
    const value = readDataByDotSeparatedString(col.field ?? "", { resource });

    if (value && ["string", "number"].includes(typeof value)) {
      return value;
    }
  } catch (e) {
    console.error(
      `Couldn't read by dot separating on:${resource}, error: ${e}`,
    );
  }

  return "";
};

const getValueFromCellRenderer = (
  col: ColDef,
  resource: AllTypeResourceItem,
  ignoreCellRenderer?: boolean,
) => {
  const canReadCellRenderer =
    !colDefsThatNoNeedToReadCellRenderer.includes(col.field ?? "") &&
    !colDefsThatNoNeedToReadCellRenderer.includes(col.colId ?? "");

  if (!canReadCellRenderer || ignoreCellRenderer) {
    return;
  }

  try {
    const params = {
      data: { resource, id: resource.id },
    } as ICellRendererParams;
    const value = col.cellRenderer?.(params);

    if (value) {
      return value;
    }
  } catch (e) {
    console.error(`Couldn't call cellRenderer on:${resource}, error: ${e}`);
  }
};

const getValueFromValueGetter = (
  col: ColDef,
  resource: AllTypeResourceItem,
) => {
  const canReadValueGetter =
    !colDefsThatNoNeedToValueGetter.includes(col.field ?? "") &&
    !colDefsThatNoNeedToValueGetter.includes(col.colId ?? "");

  if (!canReadValueGetter) {
    return;
  }

  try {
    const params = {
      data: { resource, id: resource.id },
    } as ValueGetterParams;

    const value =
      typeof col.valueGetter === "function"
        ? col.valueGetter?.(params)
        : col.valueGetter;

    if (value) {
      return value;
    }
  } catch (e) {
    console.error(`Couldn't call valueGetter on:${resource}, error: ${e}`);
  }
};
